import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, CalendarCheck, CalendarClock, CalendarX, CreditCard, Clock, RefreshCw, Trash2, Settings } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ScrollArea } from '../components/ui/scroll-area';
import { api } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';

const TYPE_ICON = {
  booking_confirmed: CalendarCheck,
  booking_rescheduled: CalendarClock,
  booking_cancelled: CalendarX,
  new_booking: CalendarCheck,
  payment_received: CreditCard,
  reminder: Clock,
  return_reminder: RefreshCw,
};

function timeAgo(iso, language) {
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then);
    const min = Math.floor(diff / 60000);
    if (min < 1) return language === 'pt' ? 'agora' : 'now';
    if (min < 60) return `${min}m`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24);
    return `${d}d`;
  } catch {
    return '';
  }
}

export const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { language } = useLanguage();
  const timerRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications');
      setNotifications(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    timerRef.current = setInterval(fetchNotifications, 30000);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [fetchNotifications]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  const handleClickItem = async (n) => {
    if (!n.read) {
      setNotifications((prev) => prev.map((x) => (x.notification_id === n.notification_id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
      try { await api.put(`/notifications/${n.notification_id}/read`); } catch { /* noop */ }
    }
    setOpen(false);
    if (n.url) navigate(n.url);
  };

  const handleMarkAll = async () => {
    setNotifications((prev) => prev.map((x) => ({ ...x, read: true })));
    setUnread(0);
    try { await api.put('/notifications/read-all'); } catch { /* noop */ }
  };

  const handleClearAll = async () => {
    setNotifications([]);
    setUnread(0);
    try { await api.delete('/notifications'); } catch { /* noop */ }
  };

  const goToCenter = () => {
    setOpen(false);
    navigate('/notificacoes');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-full hover:bg-[#F1EEE8] transition-colors"
          data-testid="notification-bell"
          aria-label="Notificações"
        >
          <Bell className="w-5 h-5 text-[#2C2924]" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#BFA05F] text-white text-[10px] font-semibold flex items-center justify-center"
              data-testid="notification-badge"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 overflow-hidden" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E6E2DA]">
          <span className="font-heading text-sm font-semibold text-[#1A1815]">
            {language === 'pt' ? 'Notificações' : 'Notifications'}
          </span>
          <div className="flex items-center gap-3">
            {unread > 0 && (
              <button
                onClick={handleMarkAll}
                className="text-xs text-[#BFA05F] hover:underline flex items-center gap-1"
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                {language === 'pt' ? 'Marcar todas' : 'Mark all'}
              </button>
            )}
            {notifications.length > 0 && (
              <button
                onClick={handleClearAll}
                className="text-xs text-[#B0483B] hover:underline flex items-center gap-1"
                data-testid="clear-all-btn"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {language === 'pt' ? 'Limpar' : 'Clear'}
              </button>
            )}
          </div>
        </div>
        <ScrollArea className="h-80">
          {notifications.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-[#A8A29A]" data-testid="notification-empty">
              {language === 'pt' ? 'Sem notificações por agora.' : 'No notifications yet.'}
            </div>
          ) : (
            notifications.map((n) => {
              const Icon = TYPE_ICON[n.type] || Bell;
              return (
                <button
                  key={n.notification_id}
                  onClick={() => handleClickItem(n)}
                  data-testid={`notification-item-${n.notification_id}`}
                  className={`w-full text-left flex gap-3 px-4 py-3 border-b border-[#F1EEE8] hover:bg-[#FBF9F4] transition-colors ${
                    n.read ? 'opacity-70' : 'bg-[#FBF9F4]'
                  }`}
                >
                  <span className="mt-0.5 flex-shrink-0">
                    <Icon className={`w-4 h-4 ${n.read ? 'text-[#A8A29A]' : 'text-[#BFA05F]'}`} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-[#1A1815] truncate">{n.title}</span>
                      <span className="text-[10px] text-[#A8A29A] flex-shrink-0">{timeAgo(n.created_at, language)}</span>
                    </span>
                    <span className="block text-xs text-[#635E54] mt-0.5 line-clamp-2">{n.body}</span>
                  </span>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-[#BFA05F] mt-1.5 flex-shrink-0" />}
                </button>
              );
            })
          )}
        </ScrollArea>
        <button
          onClick={goToCenter}
          className="w-full flex items-center justify-center gap-1.5 px-4 py-3 border-t border-[#E6E2DA] text-xs font-medium text-[#635E54] hover:bg-[#FBF9F4] transition-colors"
          data-testid="open-notification-center-btn"
        >
          <Settings className="w-3.5 h-3.5" />
          {language === 'pt' ? 'Central de notificações' : 'Notification center'}
        </button>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
